import {
  KeyValueCustomMultipleSelect,
  KeyValueCustomSelect,
  Pill,
  Text,
} from "@smartsuite/react-ui/lib";
import { useLinkedRecords } from "../../hooks/useLinkedRecords";
import { LinkedRecordFormValue } from "../../../linked-record.config";
import { $t } from "../../../../../../utils/intl";
import { fieldsStateText, fieldsText } from "../../../../fields.text";
import {
  FormConfigApplicationStructureItem,
  FormConfigItem,
  LinkedRecordObject,
} from "../../../../../../types/form";
import { UseFormFieldResult } from "../../../../../../hooks/useFormField";
import { useEffect, useState, useCallback } from "react";
import { SimpleRecord } from "@smartsuite/types";
import { getSortedRecords } from "../../linked-records.helpers";
import { useFormContext } from "../../../../../../hooks/useFormContext";
import { isEqual } from "lodash";

export interface SelectLinkedRecordDropdownProps {
  field: FormConfigApplicationStructureItem;
  formField: UseFormFieldResult<LinkedRecordObject[]>;
  formItem: FormConfigItem;
  readOnly?: boolean;
  linkedRecords?: SimpleRecord[];
  handleSelectChange: (changeValue: LinkedRecordObject | LinkedRecordObject[]) => void;
}

const formatRecords = (records: SimpleRecord[]): SimpleRecord[] => {
  return records
    .map((linkedRecord) => ({
      id: linkedRecord.id,
      title: linkedRecord.title,
      linkedRecord: {
        id: linkedRecord.id,
        title: linkedRecord.title,
      },
    }))
    .sort((lr1, lr2) => lr1.title.toLowerCase().localeCompare(lr2.title.toLowerCase()));
};

const isHydratedLinkedRecordValue = (value: unknown): value is LinkedRecordObject[] =>
  Array.isArray(value) &&
  value.length > 0 &&
  value.every(
    (item): item is LinkedRecordObject =>
      typeof item === "object" &&
      item !== null &&
      typeof (item as LinkedRecordObject).title === "string"
  );

export const SelectLinkedRecordDropdown: React.FunctionComponent<
  SelectLinkedRecordDropdownProps
> = ({ field, formItem, formField, readOnly, handleSelectChange, linkedRecords }) => {
  const [dropdownRecords, setDropdownRecords] = useState<SimpleRecord[] | null>(
    isHydratedLinkedRecordValue(formField.value)
      ? formatRecords(formField.value as unknown as SimpleRecord[])
      : null
  );

  const [hasOpened, setHasOpened] = useState(false);

  const handleRecordsChange = useCallback((records: SimpleRecord[]): void => {
    const formattedRecords = formatRecords(records);

    setDropdownRecords(formattedRecords);
  }, []);

  const handleChangeFocused = useCallback(
    (focused: boolean): void => {
      if (focused) setHasOpened(true);
      formField.onChangeFocused(focused);
    },
    [formField.onChangeFocused]
  );

  const { preferences } = useFormContext();

  const {
    isMultiple,
    isRecordsLoading,
    linkedApplicationFields,
    selectableRecords: fetchedRecords,
  } = useLinkedRecords({
    field,
    formField,
    formItem,
    enabled: hasOpened,
  });

  useEffect(() => {
    if (!isRecordsLoading && linkedRecords && !dropdownRecords) {
      handleRecordsChange(linkedRecords);
    }
  }, [linkedRecords, isRecordsLoading, dropdownRecords, handleRecordsChange]);

  useEffect(() => {
    if (!isRecordsLoading && fetchedRecords && fetchedRecords.length > 0) {
      handleRecordsChange(fetchedRecords);
    }
  }, [fetchedRecords, isRecordsLoading, handleRecordsChange]);

  useEffect(() => {
    if (!isRecordsLoading && dropdownRecords && linkedApplicationFields) {
      const sortedRecords = getSortedRecords({
        records: dropdownRecords,
        formItem,
        field,
        linkedApplicationFields,
        preferences,
      });

      if (!isEqual(sortedRecords, dropdownRecords)) {
        setDropdownRecords(sortedRecords);
      }
    }
  }, [dropdownRecords, linkedApplicationFields, isRecordsLoading, formItem, field]);

  return (
    <>
      {isMultiple && (
        <KeyValueCustomMultipleSelect<"linkedRecord", "title", LinkedRecordFormValue>
          className="linkedrecord-multiple-field-control"
          labelField="title"
          options={(dropdownRecords ?? []) as LinkedRecordFormValue[]}
          placeholder={
            isRecordsLoading
              ? $t(fieldsStateText.loading)
              : $t(fieldsText.multipleSelectPlaceholder)
          }
          disabled={readOnly}
          renderSelectedOption={(option) => <Pill text={option.title} />}
          renderOption={(option) => (
            <Text
              tag="div"
              weight="medium"
              wordBreak="break-word"
              color="currentColor"
              className="linkedrecord-multple-field-control-option"
            >
              {option.title}
            </Text>
          )}
          searchPlaceholder={$t(fieldsText.multipleSelectSearchOptionPlaceholder)}
          showFilterGreaterThan={4}
          isReadOnly={readOnly}
          state={formField.state}
          value={formField.value}
          valueField="linkedRecord"
          valueFieldPath="id"
          virtualized
          itemHeight={36}
          selectListItemsClass="linkedrecord-multiple-field-control-list"
          onChange={handleSelectChange}
          onChangeFocused={handleChangeFocused}
        />
      )}

      {!isMultiple && (
        <KeyValueCustomSelect<"linkedRecord", "title", LinkedRecordFormValue>
          isReadOnly={formItem.params.read_only}
          disabled={formItem.params.read_only}
          allowSelection={!readOnly}
          labelField="title"
          options={(dropdownRecords ?? []) as LinkedRecordFormValue[]}
          placeholder={
            isRecordsLoading ? $t(fieldsStateText.loading) : $t(fieldsText.singleSelectPlaceholder)
          }
          renderOption={(option) => (
            <Text
              tag="div"
              weight="medium"
              wordBreak="break-word"
              color="currentColor"
              className="linkedrecord-single-field-control-option"
            >
              {option.title}
            </Text>
          )}
          searchPlaceholder={$t(fieldsText.singleSelectSearchOptionPlaceholder)}
          showFilterGreaterThan={4}
          state={formField.state}
          value={formField.value?.[0]}
          valueField="linkedRecord"
          valueFieldPath="id"
          virtualized
          itemHeight={36}
          onChange={handleSelectChange}
          onChangeFocused={handleChangeFocused}
        />
      )}
    </>
  );
};
